import React, { MouseEventHandler, PropsWithChildren, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { clamp } from 'lodash';
import { useAvailableSpaceInScrollContainer } from '../../../hooks/useAvailableSpaceInScrollContainer';
import { useParentOf } from '../../../hooks/useParentOf';
import { ScrollContainer } from '../../scroll-container/ScrollContainer';
import styles from './DropdownMenu.module.scss';

export const DropdownMenu = ({
    children, onClick, isOpen, minHeight = 250, maxHeight = 400
}: Props) => {

    const container = useRef<HTMLDivElement>(null);
    const position = useRef<'top' | 'bottom'>();

    const parentRef = useParentOf(container);
    const availableSpace = useAvailableSpaceInScrollContainer(
        parentRef, verticalMarginsOf(container.current));

    useLayoutEffect(() => {
        if (container.current) {
            position.current = reposition(availableSpace, minHeight);
        }
    }, [minHeight, container, availableSpace]);

    const idealHeight = getIdealHeight(availableSpace, minHeight, maxHeight);

    const classes = classNames(styles.default, {
        [styles.hide]: !isOpen || !position.current,
        [styles[position.current!]]: position.current
    });

    return (
        <div className={classes} onClick={onClick} ref={container}>
            <ScrollContainer maxHeight={idealHeight}>
                {children}
            </ScrollContainer>
        </div>
    );
};

type Props = PropsWithChildren<{
    onClick: MouseEventHandler;
    isOpen: boolean;
    maxHeight?: number;
    minHeight?: number;
}>;

const reposition = (
    availableSpace: { below: number; above: number },
    minHeight: number
) => {
    if (availableSpace.below < minHeight && availableSpace.above > minHeight) {
        return 'top';
    } else {
        return 'bottom';
    }
};

const getIdealHeight = (
    availableSpace: { below: number; above: number },
    minHeight: number, maxHeight: number
) => {
    if (availableSpace.below > minHeight || availableSpace.above <= minHeight) {
        return clamp(availableSpace.below, minHeight, maxHeight);
    } else {
        return clamp(availableSpace.above, minHeight, maxHeight);
    }
};

const verticalMarginsOf = (element: HTMLDivElement | null) => {

    if (!element) {
        return 0;
    }

    const style = window.getComputedStyle(element);
    return parseFloat(style.getPropertyValue('margin-top'))
        + parseFloat(style.getPropertyValue('margin-bottom'));
};